import { Box, Typography, Tooltip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SettingsIcon from '@mui/icons-material/Settings';
import type { AssetSopEntry } from 'data/squares';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import useAppTheme from 'hooks/useAppTheme';

type PMType = 'week' | 'month' | 'year' | '375' | '750' | '1500';

export function getPmTitle(type: PMType): string {
  switch (type) {
    case 'week':
      return 'Weekly Maintenance';
    case 'month':
      return 'Monthly Inspections';
    case 'year':
      return 'Annual Overhaul';
    default:
      return `${type} Hrs PM`;
  }
}

export function getPmIcon(type: PMType): JSX.Element {
  const palette = clientThemePalette[CLIENT_THEME].dark;
  const commonSx = { fontSize: '2rem' };

  const iconMap: Record<string, { Icon: typeof CheckIcon; color: string }> = {
    '375': { Icon: CheckIcon, color: '#4ade80' },
    '750': { Icon: CheckIcon, color: '#4ade80' },
    '1500': { Icon: CheckIcon, color: '#4ade80' },
    week: { Icon: CalendarMonthIcon, color: palette.primary },
    month: { Icon: AccessTimeIcon, color: '#FFA403' },
    year: { Icon: SettingsIcon, color: '#EB5757' },
  };

  const { Icon, color } = iconMap[type];
  return <Icon sx={{ ...commonSx, color }} />;
}

type GoogleDocFormat = 'pdf' | 'docx';

export const getGoogleDocDownloadUrl = (
  editUrl: string,
  format: GoogleDocFormat = 'pdf'
): string => {
  const match = editUrl.match(/\/d\/([a-zA-Z0-9-_]+)/);

  if (!match) {
    throw new Error('Invalid Google Docs URL');
  }

  const docId = match[1];

  return `https://docs.google.com/document/d/${docId}/export?format=${format}`;
};

export const UpcomingPms = ({
  type,
  assetSopData,
}: {
  type: PMType;
  assetSopData: AssetSopEntry[];
}) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const title = getPmTitle(type);
  const canDownload = assetSopData.find((item) => item.type === type);

  return (
    <Box
      sx={{
        backgroundColor: palette.paperBackground,
        padding: '16px 16px',
        borderRadius: '4px',
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.4)',
        margin: '10px',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        color: palette.textPrimary,
      }}
    >
      {getPmIcon(type)}

      <Box sx={{ flexGrow: 1 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '16px',
            color: palette.textPrimary,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            color: palette.grey,
            fontSize: '12px',
          }}
        >
          {t('view_download_parts')}
        </Typography>
      </Box>

      <Tooltip
        title={
          !canDownload?.url
            ? 'This asset does not have an SOP available for download'
            : 'Download SOP'
        }
        arrow
      >
        <span>
          <DownloadIcon
            sx={{
              color: palette.grey,
              fontSize: '1.2rem',
              opacity: canDownload?.url ? 0.8 : 0.4,
              cursor: canDownload?.url ? 'pointer' : 'not-allowed',
            }}
            onClick={() => {
              if (!canDownload?.url) return;

              const downloadUrl = getGoogleDocDownloadUrl(
                canDownload.url,
                'docx'
              );

              window.open(downloadUrl, '_blank');
            }}
          />
        </span>
      </Tooltip>
    </Box>
  );
};
